import React, { useState } from "react";
import { useIntl } from "react-intl";
import { Dropdown, Tooltip } from "antd";
import {
  RiExpandUpDownLine,
  RiPencilLine,
  RiMoreLine,
  RiLoginCircleLine,
  RiDeleteBin2Line,
} from "react-icons/ri";

import { convertToDateOnly } from "../../../utils/date";
import cnpjCpfFormatter from "../../../utils/cnpjCpfFormatter";
import formatServiceTypeLabel from "../../../utils/formatServiceTypeLabel";
import Pagination from "../../../newUXComponents/Pagination";

import {
  TableWrapper,
  TableHead,
  HeadCell,
  SortIcon,
  Row,
  Cell,
  TagsCell,
  ServiceTag,
  ActionsCell,
  IconBtn,
  ActionsMenu,
  ActionsMenuItem,
} from "./styles";

const COL = {
  name: 2,
  document: 1.4,
  serviceTypes: 2.2,
  exhibitorAccess: 1.2,
  associationDate: 1.2,
};
const ACTIONS_FLEX = "0 0 88px";
const TOOLTIP_STYLE = { borderRadius: 6 };

const formatDocument = (document) =>
  document?.length > 9 ? cnpjCpfFormatter(document, "J") : document;

const RowActions = ({ record, onEdit, onEditPermission, onDelete }) => {
  const intl = useIntl();
  const [open, setOpen] = useState(false);

  const overlay = (
    <ActionsMenu>
      <ActionsMenuItem
        type="button"
        onClick={() => {
          setOpen(false);
          onEditPermission(record);
        }}
      >
        <RiLoginCircleLine size={16} />
        {intl.formatMessage({
          id: record.allowManageAccount
            ? "revoke_exhibitor_access"
            : "grant_exhibitor_access",
        })}
      </ActionsMenuItem>
      <ActionsMenuItem
        type="button"
        $danger
        onClick={() => {
          setOpen(false);
          onDelete(record);
        }}
      >
        <RiDeleteBin2Line size={16} />
        {intl.formatMessage({ id: "delete_membership" })}
      </ActionsMenuItem>
    </ActionsMenu>
  );

  return (
    <>
      <Tooltip
        title={intl.formatMessage({ id: "edit_provider" })}
        overlayInnerStyle={TOOLTIP_STYLE}
      >
        <IconBtn type="button" onClick={() => onEdit(record)}>
          <RiPencilLine size={16} />
        </IconBtn>
      </Tooltip>
      <Dropdown
        dropdownRender={() => overlay}
        trigger={["click"]}
        open={open}
        onOpenChange={setOpen}
        placement="bottomRight"
      >
        <IconBtn type="button">
          <RiMoreLine size={18} />
        </IconBtn>
      </Dropdown>
    </>
  );
};

const AssociatedProvidersTable = ({
  dataSource = [],
  onEdit,
  onEditPermission,
  onDelete,
  tablePagination = {},
  onPageChange = () => {},
  sortDirection,
  onSortByName = () => {},
}) => {
  const intl = useIntl();

  return (
    <TableWrapper>
      <TableHead>
        <HeadCell $flex={COL.name} $sortable onClick={onSortByName}>
          {intl.formatMessage({ id: "provider_name_column" })}
          <SortIcon $active={!!sortDirection} $direction={sortDirection}>
            <RiExpandUpDownLine size={14} />
          </SortIcon>
        </HeadCell>
        <HeadCell $flex={COL.document}>
          {intl.formatMessage({ id: "document_or_cnpj" })}
        </HeadCell>
        <HeadCell $flex={COL.serviceTypes}>
          {intl.formatMessage({ id: "service_types" })}
        </HeadCell>
        <HeadCell $flex={COL.exhibitorAccess}>
          {intl.formatMessage({ id: "exhibitor_access" })}
        </HeadCell>
        <HeadCell $flex={COL.associationDate}>
          {intl.formatMessage({ id: "provider_association_date" })}
        </HeadCell>
        <HeadCell $flex={ACTIONS_FLEX} />
      </TableHead>

      {dataSource.map((record) => (
        <Row key={record.id}>
          <Cell $flex={COL.name}>{record.serviceProviderName}</Cell>
          <Cell $flex={COL.document}>{formatDocument(record.document)}</Cell>
          <TagsCell $flex={COL.serviceTypes}>
            {(record.serviceTypes || []).map((serviceType) => (
              <ServiceTag key={serviceType.id}>
                {formatServiceTypeLabel(serviceType.name)}
              </ServiceTag>
            ))}
          </TagsCell>
          <Cell $flex={COL.exhibitorAccess}>
            {intl.formatMessage({
              id: record.allowManageAccount ? "yes" : "no",
            })}
          </Cell>
          <Cell $flex={COL.associationDate}>
            {record.creationDate ? convertToDateOnly(record.creationDate) : " - "}
          </Cell>
          <ActionsCell $flex={ACTIONS_FLEX}>
            <RowActions
              record={record}
              onEdit={onEdit}
              onEditPermission={onEditPermission}
              onDelete={onDelete}
            />
          </ActionsCell>
        </Row>
      ))}

      <Pagination
        current={tablePagination.current || 1}
        pageSize={tablePagination.pageSize || 10}
        total={tablePagination.total || 0}
        onChange={(page, size) => onPageChange(page - 1, size)}
      />
    </TableWrapper>
  );
};

export default AssociatedProvidersTable;
